/**
 * Pagina /clienti — lista clienti + form creazione.
 *
 * Visibilità:
 *  - Tabella: tutti gli utenti autenticati (VIEW_PORTFOLIO).
 *  - Form "Nuovo cliente": solo chi ha PORTFOLIO_MANAGEMENT.
 *
 * Gating UI tramite usePermissions().can('PORTFOLIO_MANAGEMENT').
 */

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../auth/index.js';
import { useCustomers, useCreateCustomer } from './hooks.js';
import type { CreateCustomerInput } from './types.js';
import styles from './Customers.module.css';

/** Form di creazione cliente: visibile solo con PORTFOLIO_MANAGEMENT. */
function NuovoClienteForm({ onCreato }: { onCreato: () => void }) {
  const [name, setName] = useState('');
  const [externalRef, setExternalRef] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');

  const { mutate, isPending, isError, error } = useCreateCustomer();

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const input: CreateCustomerInput = {
      name: name.trim(),
      ...(externalRef.trim() ? { externalRef: externalRef.trim() } : {}),
      ...(websiteUrl.trim() ? { websiteUrl: websiteUrl.trim() } : {}),
    };
    mutate(input, {
      onSuccess: () => {
        setName('');
        setExternalRef('');
        setWebsiteUrl('');
        onCreato();
      },
    });
  }

  return (
    <div className={styles.formCard} data-testid="form-nuovo-cliente">
      <p className={styles.formTitle}>Nuovo cliente</p>
      <form onSubmit={handleSubmit}>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel} htmlFor="cliente-name">
              Nome *
            </label>
            <input
              id="cliente-name"
              className={styles.formInput}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="es. Acme S.r.l."
              data-testid="input-cliente-name"
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel} htmlFor="cliente-externalRef">
              Riferimento esterno
            </label>
            <input
              id="cliente-externalRef"
              className={styles.formInput}
              value={externalRef}
              onChange={(e) => setExternalRef(e.target.value)}
              placeholder="es. CRM-12345"
              data-testid="input-cliente-externalRef"
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel} htmlFor="cliente-websiteUrl">
              Sito web
            </label>
            <input
              id="cliente-websiteUrl"
              className={styles.formInput}
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="es. https://acme.example.com"
              data-testid="input-cliente-websiteUrl"
            />
          </div>
        </div>
        <div className={styles.formActions}>
          <button
            type="submit"
            className={styles.btnPrimary}
            disabled={isPending}
            data-testid="btn-crea-cliente"
          >
            {isPending ? 'Creazione…' : 'Crea cliente'}
          </button>
          {isError && (
            <span className={styles.inlineMutError} data-testid="errore-crea-cliente">
              {error instanceof Error ? error.message : 'Errore durante la creazione'}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

/** Pagina principale lista clienti. */
export function CustomersListPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { data: clienti, isLoading, isError, error } = useCustomers();

  const puoGestire = can('PORTFOLIO_MANAGEMENT');

  return (
    <div data-testid="customers-list-page">
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Clienti</h1>
      </div>

      {/* Form nuovo cliente — solo per chi ha PORTFOLIO_MANAGEMENT */}
      {puoGestire && <NuovoClienteForm onCreato={() => {}} />}

      {/* Tabella clienti */}
      {isLoading && (
        <p className={styles.loadingMsg} data-testid="loading-clienti">
          Caricamento clienti…
        </p>
      )}

      {isError && (
        <p className={styles.errorMsg} data-testid="errore-clienti">
          {error instanceof Error ? error.message : 'Errore nel caricamento dei clienti'}
        </p>
      )}

      {!isLoading && !isError && clienti !== undefined && clienti.length === 0 && (
        <div className={styles.emptyState} data-testid="lista-clienti-vuota">
          Nessun cliente registrato. {puoGestire ? 'Usa il form sopra per crearne uno.' : ''}
        </div>
      )}

      {!isLoading && !isError && clienti !== undefined && clienti.length > 0 && (
        <table className={styles.table} data-testid="tabella-clienti">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Riferimento esterno</th>
              <th>Sito web</th>
              <th>Creato il</th>
            </tr>
          </thead>
          <tbody>
            {clienti.map((c) => (
              <tr key={c.id} data-testid={`riga-cliente-${c.id}`}>
                <td>
                  <button
                    className={styles.linkCell}
                    onClick={() => navigate(`/customers/${c.id}`)}
                    data-testid={`link-cliente-${c.id}`}
                  >
                    {c.name}
                  </button>
                </td>
                <td>{c.externalRef ?? '—'}</td>
                <td>
                  {c.websiteUrl ? (
                    <a href={c.websiteUrl} target="_blank" rel="noreferrer">
                      {c.websiteUrl}
                    </a>
                  ) : (
                    '—'
                  )}
                </td>
                <td>{c.createdAt}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
